import {createAsyncThunk, createSlice, miniSerializeError} from '@reduxjs/toolkit';

import {bookPollForCalendar} from './HostAppointmentsStore';

import {merge, strEq} from '../Utils/index';

import {
  attachScope,
  clearAdhocReservedEvents,
  createOneOffAppointment,
  deleteEvent,
  deletePendingEvent,
  editAvailabilityPoll,
  editBooking,
  getPendingEvents,
  listBookings,
  removeBookerFromEventNew,
  rescheduleEventNew,
} from 'Api/ZCalendar';
import {HttpError} from 'Error/HttpError';
import {EVENT_STATUS, USER_RESPONSE_STATUS} from 'Utils/consts';

const initialState = {
  pageToken: null,
  bookings: [],
  pendingItems: {
    items: [],
    isLoading: false,
  },
  isLoading: false,
  errorState: {
    isError: false,
    reason: '',
  },
  nextPageToken: null,
  latestRequestId: null,
  isTabKeyEnabled: true, // used for a11y
};

export const listBookingsForCalendar = createAsyncThunk(
  'hostBookings/listByCalendar',
  async ({asScope, query}, thunkAPI) => {
    const response = await listBookings(asScope, query);
    return response;
  }
);

export const editBookingsForCalendar = createAsyncThunk(
  'hostBookings/editBooking',
  /**
   *
   * @param {{
   *  asScope: Scope,
   *  data: any,
   *  eventId: string,
   * }} param0
   * @param {*} thunkAPI
   * @return {Promise}
   */
  async ({asScope, data, eventId}, thunkAPI) => {
    const response = await editBooking(asScope, eventId, data);
    return response;
  }
);

export const deleteBookingForCalendar = createAsyncThunk(
  'hostBookings/deleteBooking',
  async ({asScope, comment, eventId}, thunkAPI) => {
    await deleteEvent(asScope, eventId, comment);
  }
);

export const cancelEventAndRefund = createAsyncThunk(
  'hostBookings/cancelEventAndRefund',
  async ({asScope, comment, eventId}, thunkAPI) => {
    await deleteEvent(asScope, eventId, comment);
  }
);

export const refundBookingForCalendar = createAsyncThunk(
  'hostBookings/refundBookingForCalendar',
  async ({asScope, comment, eventId}, thunkAPI) => {
    await deleteEvent(asScope, eventId, comment, 'onlyRefund');
  }
);

export const deleteEventBookerForCalendar = createAsyncThunk(
  'hostBookings/removeBooker',
  async ({asScope, attendeeId, data, eventId}, thunkAPI) => {
    // eslint-disable-next-line no-unused-vars
    const {email, ...restData} = data;
    await removeBookerFromEventNew(asScope, attendeeId, restData);
  }
);

export const deleteEventBookerAndRefund = createAsyncThunk(
  'hostBookings/removeBookerAndRefund',
  async ({asScope, attendeeId, data, eventId}, thunkAPI) => {
    // eslint-disable-next-line no-unused-vars
    const {email, ...restData} = data;
    await removeBookerFromEventNew(asScope, attendeeId, restData);
  }
);

export const refundEventBookerForCalendar = createAsyncThunk(
  'hostBookings/refundBooker',
  async ({asScope, attendeeId, data, eventId}, thunkAPI) => {
    // eslint-disable-next-line no-unused-vars
    const {email, ...restData} = data;
    restData.action = 'onlyRefund';
    await removeBookerFromEventNew(asScope, attendeeId, restData);
  }
);

export const rescheduleBookedEventForAttendee = createAsyncThunk(
  'hostBookings/rescheduleBookedEventForAttendee',
  async ({asScope, attendeeId, rescheduleData}, thunkAPI) => {
    const payload = {...rescheduleData};
    const newEventData = await rescheduleEventNew(attendeeId, payload, {});
    return {attendeeId, newEventData, rescheduleData};
  }
);

export const listPendingForCalendar = createAsyncThunk(
  'hostBookings/listPending',
  /**
   *
   * @param {{query: ?Object}} param0
   * @param {*} thunkAPI
   * @return {Promise}
   */
  async ({query}, thunkAPI) => {
    const response = await getPendingEvents(query);
    return response;
  }
);

export const removePendingEvent = createAsyncThunk(
  'hostBookings/removePendingEvent',
  async ({asScope, pendingEventId}, thunkAPI) => {
    const response = await deletePendingEvent(asScope, pendingEventId);
    return response;
  }
);

export const clearReservedEvents = createAsyncThunk(
  'hostBookings/clearReservedEvents',
  async ({pendingEventId}, thunkAPI) => {
    await clearAdhocReservedEvents(pendingEventId);
  },
  {
    serializeError: (e) => {
      if (e instanceof HttpError) {
        return {
          ...miniSerializeError(e),
          body: e.body,
          errorCode: e.errorCode,
        };
      }
      return miniSerializeError(e);
    },
  }
);

export const editPollForCalendar = createAsyncThunk(
  'hostBookings/editPoll',
  async ({asScope, clearReservedEvents, data, notifyVoters, pollId, reFetchAdhocEvents}, thunkAPI) => {
    if (clearReservedEvents) {
      // must clear reserved events BEFORE patching the availability poll, else references may be lost already.
      await clearAdhocReservedEvents(pollId);
    }
    let response = await editAvailabilityPoll(asScope, pollId, data, notifyVoters);
    if (reFetchAdhocEvents) {
      const recentPending = await getPendingEvents(attachScope(asScope, {}));
      response = await recentPending.items.find((event) => event.id === pollId);
    }
    return response;
  },
  {
    serializeError: (e) => {
      if (e instanceof HttpError) {
        return {
          ...miniSerializeError(e),
          body: e.body,
          errorCode: e.errorCode,
        };
      }
      return miniSerializeError(e);
    },
  }
);

export const createOneOffEvent = createAsyncThunk(
  'hostBookings/createOneOffEvent',
  async ({asScope, data, reFetchAdhocEvents}, thunkAPI) => {
    let response = await createOneOffAppointment(asScope, data);
    if (reFetchAdhocEvents) {
      const recentPending = await getPendingEvents(attachScope(asScope, {}));
      response = await recentPending.items.find((event) => event.id === response.id);
    }
    return response;
  }
);

export const hostBookingsStore = createSlice({
  name: 'HostBookingsStore',
  initialState,
  reducers: {
    addOneOffPendingEvent: (state, action) => {
      state.pendingItems.items.push(action.payload);
    },
    removeOneOffPendingEvent: (state, action) => {
      state.pendingItems.items = state.pendingItems.items.filter((item) => !strEq(item.id, action.payload.id));
    },
    setTabKeyEnabled: (state, action) => {
      state.isTabKeyEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listBookingsForCalendar.fulfilled, (state, action) => {
      // In order to avoid racing conditions when switching from upcoming to previous tabs
      // this request must only be fulfilled for the latest call.
      if (action.meta.requestId === state.latestRequestId) {
        state.isLoading = false;
        if (action.meta.arg.query.pageToken) {
          const includeCancelled = action.meta.arg.query.showDeleted;
          if (includeCancelled) {
            state.bookings = merge(state.bookings, action.payload.items, 'id');
          } else {
            state.bookings = merge(state.bookings,
              action.payload.items.filter((appt) => appt.status !== EVENT_STATUS.CANCELLED), 'id');
          }
        } else {
          state.bookings = action.payload.items;
        }
        state.pageToken = action.payload.nextPageToken;
        state.errorState = {
          isError: false,
          reason: '',
        };
      }
    });
    builder.addCase(listBookingsForCalendar.pending, (state, action) => {
      state.latestRequestId = action.meta.requestId;
      state.isLoading = true;
      state.errorState = {
        isError: false,
        reason: '',
      };
    });
    builder.addCase(listBookingsForCalendar.rejected, (state, action) => {
      state.isLoading = false;
      state.errorState = {
        isError: true,
        reason: action.error.message === 'Permission of team denied'?
          'storeErrors.permissionOfTeamDenied': 'storeErrors.listBookingsForCalendar',
      };
    });

    builder.addCase(editBookingsForCalendar.fulfilled, (state, action) => {
      const matchId = (element) => element.id === action.payload.id;
      const index = state.bookings.findIndex(matchId);
      state.bookings[index] = action.payload;
    });
    builder.addCase(editBookingsForCalendar.pending, (state, action) => {
      state.latestRequestId = action.meta.requestId;
      state.errorState = {
        isError: false,
        reason: '',
      };
    });
    builder.addCase(editBookingsForCalendar.rejected, (state, action) => {
      state.errorState = {
        isError: true,
        reason: 'Error updating booking',
      };
    });

    builder.addCase(deleteBookingForCalendar.fulfilled, (state, action) => {
      state.bookings = state.bookings.filter((event) => action.meta.arg.eventId !== event.id);
    });

    builder.addCase(cancelEventAndRefund.fulfilled, (state, action) => {
      state.bookings = state.bookings.filter((event) => action.meta.arg.eventId !== event.id);
    });

    builder.addCase(refundBookingForCalendar.fulfilled, (state, action) => {
      const eventIndex = state.bookings.findIndex((event) => event.id === action.meta.arg.eventId);
      if (eventIndex > -1) {
        state.bookings[eventIndex].refunded = true;
        state.bookings[eventIndex].attendees = state.bookings[eventIndex].attendees.map((attendee) => {
          return {...attendee, refunded: true};
        });
      }
    });

    builder.addCase(deleteEventBookerForCalendar.fulfilled, (state, action) => {
      const eventIndex = state.bookings.findIndex((event) => event.id === action.meta.arg.eventId);
      if (eventIndex > -1) {
        const deletedBookerEmail = action.meta.arg.data.email;
        const userIndex = state.bookings[eventIndex].attendees.findIndex((user) => user.email === deletedBookerEmail);
        const storeEventUser = state.bookings[eventIndex].attendees[userIndex];
        storeEventUser.responseStatus = USER_RESPONSE_STATUS.DECLINED;
        storeEventUser.comment = action.meta.arg.data.comment || '';
        storeEventUser.booker = false;

        // Check if all attendees have cancelled - if so, mark the event as cancelled
        const event = state.bookings[eventIndex];
        const bookers = event.attendees.filter((attendee) => !attendee.host);
        const allBookersCancelled = bookers.every((booker) =>
          booker.responseStatus === USER_RESPONSE_STATUS.DECLINED
        );

        if (allBookersCancelled && bookers.length > 0) {
          // Mark the event as cancelled
          event.status = 'cancelled';
        }
      } else {
        console.error('rdx: no matching event booking found');
      }
    });

    // Update UI after removing attendee and refunding
    builder.addCase(deleteEventBookerAndRefund.fulfilled, (state, action) => {
      const eventIndex = state.bookings.findIndex((event) => event.id === action.meta.arg.eventId);
      if (eventIndex > -1) {
        const deletedBookerEmail = action.meta.arg.data.email;
        const userIndex = state.bookings[eventIndex].attendees.findIndex((user) => user.email === deletedBookerEmail);
        const storeEventUser = state.bookings[eventIndex].attendees[userIndex];
        storeEventUser.responseStatus = USER_RESPONSE_STATUS.DECLINED;
        storeEventUser.comment = action.meta.arg.data.comment || '';
        storeEventUser.booker = false;

        // Check if all attendees have cancelled - if so, mark the event as cancelled
        const event = state.bookings[eventIndex];
        const bookers = event.attendees.filter((attendee) => !attendee.host);
        const allBookersCancelled = bookers.every((booker) =>
          booker.responseStatus === USER_RESPONSE_STATUS.DECLINED
        );

        if (allBookersCancelled && bookers.length > 0) {
          // Mark the event as cancelled
          event.status = 'cancelled';
        }
      } else {
        console.error('rdx: no matching event booking found');
      }
    });

    // Update UI after refunding attendee
    builder.addCase(refundEventBookerForCalendar.fulfilled, (state, action) => {
      const eventIndex = state.bookings.findIndex((event) => event.id === action.meta.arg.eventId);
      if (eventIndex > -1) {
        const deletedBookerEmail = action.meta.arg.data.email;
        const userIndex = state.bookings[eventIndex].attendees.findIndex((user) => user.email === deletedBookerEmail);
        const storeEventUser = state.bookings[eventIndex].attendees[userIndex];
        // `only refund` will not change the response status of the booker, just set the refunded status to true
        storeEventUser.refunded = true;
      } else {
        console.error('rdx: no matching event booking found');
      }
    });

    // Update UI after attendee reschedules their booking
    builder.addCase(rescheduleBookedEventForAttendee.fulfilled, (state, action) => {
      const {attendeeId, newEventData} = action.payload;

      // Find the event by event ID first, then by attendee ID as fallback
      let eventIndex = state.bookings.findIndex((event) => event.id === newEventData.id);

      if (eventIndex === -1) {
        // Fallback: find by attendee ID
        eventIndex = state.bookings.findIndex((event) =>
          event.attendees && event.attendees.some((attendee) => attendee.id === attendeeId)
        );
      }

      if (eventIndex > -1) {
        const oldEvent = state.bookings[eventIndex];

        // If the new event has a different ID, remove the old event and add the new one
        if (newEventData.id && newEventData.id !== oldEvent.id) {
          // Remove the old event
          state.bookings.splice(eventIndex, 1);
          // Add the new event
          state.bookings.push(newEventData);
        } else {
          // Update the existing event with the new rescheduled data
          state.bookings[eventIndex] = {
            ...state.bookings[eventIndex],
            ...newEventData,
            // Preserve existing attendees structure but update the rescheduled attendee
            attendees: state.bookings[eventIndex].attendees.map((attendee) => {
              if (attendee.id === attendeeId) {
                return {
                  ...attendee,
                  // Update any relevant fields from the new event data
                  ...(newEventData.attendees && newEventData.attendees.find((a) => a.id === attendeeId)),
                };
              }
              return attendee;
            }),
          };
        }
      } else {
        console.error('rdx: no matching event booking found');
      }
    });

    builder.addCase(listPendingForCalendar.pending, (state, action) => {
      state.nextPageToken = null;
      state.pendingItems.isLoading = true;
      state.errorState = {
        isError: false,
        reason: '',
      };
    });

    builder.addCase(listPendingForCalendar.fulfilled, (state, action) => {
      state.pendingItems.items = action.payload.items;
      state.pendingItems.isLoading = false;
      state.errorState = {
        isError: false,
        reason: '',
      };
    });

    builder.addCase(listPendingForCalendar.rejected, (state, action) => {
      state.pendingItems.isLoading = false;
      state.errorState = {
        isError: true,
        reason: 'storeErrors.listBookingsForCalendar',
      };
    });

    builder.addCase(removePendingEvent.pending, (state, action) => {
      state.pendingItems.isLoading = true;
    });

    builder.addCase(removePendingEvent.fulfilled, (state, action) => {
      state.pendingItems.isLoading = false;
      state.pendingItems.items = state.pendingItems.items.filter(
        (pendingEvent) => action.meta.arg.pendingEventId !== pendingEvent.id
      );
    });

    builder.addCase(clearReservedEvents.fulfilled, (state, action) => {
      const itemIdx = state.pendingItems.items.findIndex(
        (pendingEvent) => action.meta.arg.pendingEventId === pendingEvent.id
      );
      if (itemIdx >= 0) {
        state.pendingItems.items[itemIdx].reserveTimes = false;
        state.pendingItems.items[itemIdx].reservedEvents = null;
      }
    });

    builder.addCase(editPollForCalendar.fulfilled, (state, action) => {
      const idx = state.pendingItems.items.findIndex((item) => item.id === action.payload.id);
      state.pendingItems.items[idx] = {...action.payload};
    });

    builder.addCase(createOneOffEvent.pending, (state, action) => {
      state.pendingItems.isLoading = true;
    });

    builder.addCase(createOneOffEvent.fulfilled, (state, action) => {
      state.pendingItems.isLoading = false;
      state.errorState = {
        isError: false,
        reason: '',
      };
    });

    builder.addCase(createOneOffEvent.rejected, (state, action) => {
      state.pendingItems.isLoading = false;
      state.errorState = {
        isError: true,
        reason: 'storeErrors.listBookingsForCalendar',
      };
    });

    builder.addCase(removePendingEvent.rejected, (state, action) => {
      state.pendingItems.isLoading = false;
    });
    builder.addCase(bookPollForCalendar.fulfilled, (state, action) => {
      state.pendingItems.isLoading = false;
      state.pendingItems.items = state.pendingItems.items.filter(
        (pendingEvent) => action.meta.arg.pendingEventId !== pendingEvent.id
      );
    });
    builder.addCase(bookPollForCalendar.pending, (state, action) => {
      state.pendingItems.isLoading = true;
    });
  },
});

export const {addOneOffPendingEvent, removeOneOffPendingEvent, setTabKeyEnabled} = hostBookingsStore.actions;
export default hostBookingsStore.reducer;
